import React, { useCallback, useEffect, useMemo } from 'react';
import { EntityListImportAction } from '@ydistri/api-sdk';
import { useGetEntityListImportSummaryQuery } from '../../apiEntityLists';
import { QueryStatus, skipToken } from '@reduxjs/toolkit/query';
import { useSelector } from 'react-redux';
import { ReduxState, useAppDispatch } from '../../../../../store';
import StatisticsItemComponent from '../../../common/validation-results/StatisticsItemComponent';
import { Space, Tooltip } from 'antd';
import { StatisticsItem, StatisticsItemType } from '../../../common/administrationItemsTypes';
import { setSelectedValidationImportAction } from '../../entityListAdministrationSlice';
import EntityListItemImportGrid from './EntityListItemImportGrid';
import { ActionsBar, Button, Column, Row, Text } from '@ydistri/ds';
import {
  EntityListTypeSupported,
  getEntityListSpecification,
  IMPORT_STEPS,
  isSkuBasedItemType,
} from '../../entityListsLib';
import { useSetImportStep } from '../../hooks/useSetImportStep';
import { useCurrentEntityListState } from '../../hooks/useCurrentEntityListState';
import useDeleteEntityListImport from '../../hooks/useDeleteEntityListImport';
import { AvailableModals, openModal } from '../../../../Modals/modalSlice';
import { useFinalizeEntityListImport } from '../../hooks/useFinalizeEntityListImport';
import EnabledSkusStatsComponent from './components/EnabledSkusStatsComponent';
import ImportWaitingHint from '../../components/ImportWaitingHint';

interface EntityListStepValidationProps {
  entityListType: EntityListTypeSupported;
}

type ImportActionMapValue = Pick<StatisticsItem, 'label' | 'type'> & { position: number };
const importActionMap: Record<EntityListImportAction, ImportActionMapValue> = {
  [EntityListImportAction.NotSet]: {
    label: 'Not set',
    type: StatisticsItemType.ERROR,
    position: 8,
  },
  [EntityListImportAction.Skip]: {
    label: 'Skip',
    type: StatisticsItemType.ERROR,
    position: 2,
  },
  [EntityListImportAction.Insert]: {
    label: 'New records',
    type: StatisticsItemType.SUCCESS,
    position: 3,
  },
  [EntityListImportAction.Update]: {
    label: 'Update',
    type: StatisticsItemType.SUCCESS,
    position: 5,
  },
  [EntityListImportAction.CreateSkuAndInsert]: {
    label: 'New SKU',
    type: StatisticsItemType.SUCCESS,
    position: 4,
  },
  [EntityListImportAction.Duplicate]: {
    label: 'Duplicates',
    type: StatisticsItemType.ERROR,
    position: 6,
  },
  [EntityListImportAction.NoChange]: {
    label: 'No change',
    type: StatisticsItemType.UNCHANGED,
    position: 7,
  },
  [EntityListImportAction.Delete]: {
    label: 'Remove',
    type: StatisticsItemType.REMOVED,
    position: 1,
  },
};

const convertImportActionToStatisticsItem = (
  a: EntityListImportAction,
  count: number,
): StatisticsItem => {
  return {
    observation: count,
    label: importActionMap[a].label,
    type: importActionMap[a].type,
  };
};

const EntityListStepValidation: React.FC<EntityListStepValidationProps> = ({ entityListType }) => {
  const setStep = useSetImportStep(entityListType);
  const { finalize, pending: finalizing } = useFinalizeEntityListImport(entityListType);
  const dispatch = useAppDispatch();
  const selectedEntityList = useSelector(
    (state: ReduxState) => state.entityListsAdministration[entityListType].selectedEntityList,
  );
  const [deleteEntityListImportHandler, deleteStatus] = useDeleteEntityListImport(entityListType);

  const entityListState = useCurrentEntityListState(entityListType);

  const entityListId = selectedEntityList?.entityListId;
  const verificationTimeStamp = entityListState?.verificationTimeStamp;

  const selectedValidationImportAction = entityListState?.selectedValidationImportAction;

  const summaryQuery = useGetEntityListImportSummaryQuery(entityListId ?? skipToken);
  const {
    data: entityListImportSummary,
    isLoading: isLoadingSummary,
    isFetching,
    isError,
  } = summaryQuery;
  const validationReady =
    !isLoadingSummary &&
    !isFetching &&
    !isError &&
    !!entityListImportSummary?.ignoreValidationToken;

  const entityListSpecification = getEntityListSpecification(entityListType);

  const clickHandler = useCallback(
    (stat: EntityListImportAction) => {
      return () => dispatch(setSelectedValidationImportAction({ entityListType, data: stat }));
    },
    [dispatch, entityListType],
  );

  const summaries = useMemo(() => {
    return (
      entityListImportSummary?.summaries
        .filter(stat => stat.itemCount > 0)
        .toSorted(
          (a, b) =>
            importActionMap[a.entityListImportAction].position -
            importActionMap[b.entityListImportAction].position,
        ) ?? []
    );
  }, [entityListImportSummary]);

  useEffect(() => {
    if (!isLoadingSummary) {
      if (summaries.length > 0) {
        dispatch(
          setSelectedValidationImportAction({
            entityListType,
            data: summaries[0].entityListImportAction,
          }),
        );
      }
    }
  }, [dispatch, summaries, entityListType, isLoadingSummary]);

  const entityListEmpty = useMemo(() => {
    let sum = 0;
    const actions = [
      EntityListImportAction.Update,
      EntityListImportAction.Insert,
      EntityListImportAction.CreateSkuAndInsert,
      EntityListImportAction.NoChange,
    ];

    (entityListImportSummary?.summaries ?? []).forEach(is => {
      if (actions.includes(is.entityListImportAction)) {
        sum += is.itemCount;
      }
    });
    return sum === 0;
  }, [entityListImportSummary]);

  const handleFinalizeClick = () => {
    if (!validationReady || finalizing || !entityListId || !verificationTimeStamp) return;
    if (
      entityListImportSummary.totalSkusToBeCreated > 0 ||
      entityListImportSummary.totalSkusToBeEnabled > 0 ||
      entityListImportSummary.totalNotActiveItems > 0
    ) {
      dispatch(
        openModal({
          type: AvailableModals.ENTITY_LIST_SKU_HANDLING,
          data: { entityListType, entityListId, verificationTimeStamp },
        }),
      );
    } else {
      void finalize({
        entityListId,
        verificationTimeStamp,
        ignoreValidationToken: entityListImportSummary.ignoreValidationToken,
      });
    }
  };

  const previousStepHandler = useCallback(() => {
    setStep(IMPORT_STEPS.ITEM_IMPORT);
  }, [setStep]);

  const mutationRunning = finalizing || deleteStatus === QueryStatus.pending;

  return (
    <Column $gap="1rem" $flexWrap="nowrap" $flexGrow={1}>
      {entityListImportSummary && (
        <Space>
          {summaries.map(stat => (
            <StatisticsItemComponent
              onClick={clickHandler(stat.entityListImportAction)}
              item={convertImportActionToStatisticsItem(
                stat.entityListImportAction,
                stat.itemCount,
              )}
              selected={stat.entityListImportAction === selectedValidationImportAction}
              key={stat.entityListImportAction}
            />
          ))}
          <Tooltip title="Ignore removes items with missing or disabled current SKU attributes. Product lists count products without any enabled SKU in those attributes.">
            <div>
              <StatisticsItemComponent
                item={{
                  label: 'Not active SKUs',
                  observation: entityListImportSummary.totalNotActiveItems,
                  type: StatisticsItemType.REMOVED,
                }}
              />
            </div>
          </Tooltip>
          {isSkuBasedItemType(entityListSpecification.itemType) && (
            <EnabledSkusStatsComponent
              enabledSkusCount={entityListImportSummary.totalSkusToBeEnabled}
            />
          )}
        </Space>
      )}

      {selectedValidationImportAction && selectedEntityList?.entityListId && (
        <EntityListItemImportGrid
          entityListType={entityListType}
          selectedEntityListId={selectedEntityList.entityListId}
        />
      )}
      {!(mutationRunning || isLoadingSummary) && entityListEmpty && (
        <Text $type="danger">Entity list would be empty, it is not possible to finalize.</Text>
      )}
      {isError && (
        <Text $type="danger">
          Validation could not be loaded.{' '}
          <Button onClick={() => void summaryQuery.refetch()}>Retry validation</Button>
        </Text>
      )}
      <Row>
        <ActionsBar data-testid="EntityListEditorActions">
          <Button
            data-testid="previous-step"
            onClick={previousStepHandler}
            disabled={
              !selectedEntityList || mutationRunning || !verificationTimeStamp || entityListEmpty
            }
            $colorVariant="ghost"
          >
            &lt;&lt; Previous step
          </Button>
          <Button
            disabled={
              !selectedEntityList ||
              mutationRunning ||
              !verificationTimeStamp ||
              entityListEmpty ||
              !validationReady
            }
            data-testid="put-entity-list-items"
            type="primary"
            onClick={handleFinalizeClick}
            loading={finalizing}
          >
            Finalize {entityListSpecification.title}
          </Button>
          <Button
            disabled={!selectedEntityList || mutationRunning || !verificationTimeStamp}
            data-testid="delete-entity-list-import"
            type="default"
            onClick={deleteEntityListImportHandler}
            loading={deleteStatus === QueryStatus.pending}
            $colorVariant="ghost"
          >
            Cancel
          </Button>
        </ActionsBar>
      </Row>
      <ImportWaitingHint pending={finalizing || deleteStatus === QueryStatus.pending} />
    </Column>
  );
};

export default EntityListStepValidation;
